import {inject, Injectable, signal} from '@angular/core';
import {AnalysisService} from './analysis.service';
import {DemographicsOptionsService} from '../components/filters-report/service/demographics-options.service';

export type DemographicKey =
  | 'gender' | 'ageRange' | 'seniority' | 'workSchedule'
  | 'employeeType' | 'contractType' | 'educationLevel' | 'maritalStatus'
  | 'educationStatus' | 'position' | 'department' | 'occupation'
  | 'totalWorkExperience' | 'hasShiftRotation';

type ApiRow = {
  finalAssessment?: string | null;
  rating?: string | null;
  totalRespondents?: number | null;
} & Partial<Record<DemographicKey, string | number | boolean | null>>;

export type SimpleDatum = { name: string; value: number };
type Facets = Partial<Record<DemographicKey, SimpleDatum[]>>;

@Injectable({providedIn: 'root'})
export class OverviewDemographicsService {
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly rows = signal<ApiRow[]>([]);
  readonly facets = signal<Facets>({});
  private analysis = inject(AnalysisService);
  private catalogs = inject(DemographicsOptionsService);
  private cache = new Map<string, ApiRow[]>();

  /** Llama 1 sola vez por campaña/guía (usa cache). NO aplica filtros demográficos. este hace el groupBy */
  load(campaign: string, guideId: string) {
    const cacheKey = `${campaign}::${guideId}`;
    const cached = this.cache.get(cacheKey);
    if (cached) {
      this.rows.set(cached);
      if (this.catalogs.catalogsLoaded()) {
        this.facets.set(this.computeFacets(cached));
      }
      this.loading.set(false);
      this.error.set(null);
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const groupByCsv = [
      'gender', 'ageRange', 'seniority', 'workSchedule',
      'employeeType', 'contractType', 'educationLevel', 'maritalStatus',
      'educationStatus', 'position', 'department', 'occupation',
      'totalWorkExperience', 'hasShiftRotation'
    ].join(',');

    this.analysis.getFinalAssessmentsMultiGroup({
      campaign, guideId, groupByCsv
    }).subscribe({
      next: (rows: ApiRow[]) => {
        const safe = Array.isArray(rows) ? rows : [];
        this.rows.set(safe);
        if (this.catalogs.catalogsLoaded()) {
          this.facets.set(this.computeFacets(safe));
        }
        this.cache.set(cacheKey, safe);
        this.loading.set(false);
      },
      error: (err: Error) => {
        this.error.set(err.message || 'Error cargando distribución general');
        this.rows.set([]);
        this.facets.set({});
        this.loading.set(false);
      }
    });
  }

  // ---------- helpers de agregación ----------
  /** Normaliza el label (booleans → Sí/No; strings con trim; null/undef → '—') */
  private normalizeLabel(v: unknown): string {
    if (v === true) return 'Sí';
    if (v === false) return 'No';
    if (v === null || v === undefined) return '—';
    const s = String(v).trim();
    return s === '' ? '—' : s;
  }

  private aggregateByKey(rows: ApiRow[], key: DemographicKey, correctionMap?: Map<string, string>): SimpleDatum[] {
    const totals = new Map<string, number>();
    for (const r of rows) {
      let label = this.normalizeLabel(r[key]);
      const normalizedLabel = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

      if (correctionMap && correctionMap.has(normalizedLabel)) {
        label = correctionMap.get(normalizedLabel)!;
      }
      const count = Number(r.totalRespondents ?? 0);
      totals.set(label, (totals.get(label) ?? 0) + count);
    }
    return Array.from(totals, ([name, value]) => ({name, value}));
  }

  private fillDomain(series: SimpleDatum[], domain: string[]): SimpleDatum[] {
    const map = new Map(series.map(d => [d.name, d.value]));
    return domain.map(label => ({name: label, value: map.get(label) ?? 0}));
  }

  /** Construye todas las facetas a partir de la tabla plana del back */
  private computeFacets(rows: ApiRow[]): Facets {
    const out: Facets = {};

    const createCorrectionMap = (options: { value: string, label: string }[]) => new Map(
      options.map(o => [o.value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(), o.value])
    );

    const departmentCorrectionMap = createCorrectionMap(this.catalogs.getDepartment());
    const occupationCorrectionMap = createCorrectionMap(this.catalogs.getOccupation());

    out.gender = this.aggregateByKey(rows, 'gender');

    out.ageRange = this.fillDomain(
      this.aggregateByKey(rows, 'ageRange'),
      this.catalogs.getAgeRange().map(o => String(o.value))
    );
    out.seniority = this.fillDomain(
      this.aggregateByKey(rows, 'seniority'),
      this.catalogs.getSeniority().map(o => String(o.value))
    );

    out.workSchedule = this.aggregateByKey(rows, 'workSchedule');
    out.employeeType = this.aggregateByKey(rows, 'employeeType');
    out.contractType = this.aggregateByKey(rows, 'contractType');
    out.educationLevel = this.aggregateByKey(rows, 'educationLevel');
    out.maritalStatus = this.aggregateByKey(rows, 'maritalStatus');
    out.educationStatus = this.aggregateByKey(rows, 'educationStatus');
    out.position = this.aggregateByKey(rows, 'position');
    out.department = this.aggregateByKey(rows, 'department', departmentCorrectionMap);
    out.occupation = this.aggregateByKey(rows, 'occupation', occupationCorrectionMap);
    out.totalWorkExperience = this.aggregateByKey(rows, 'totalWorkExperience');
    out.hasShiftRotation = this.aggregateByKey(rows, 'hasShiftRotation');

    return out;
  }
}
